/**
 * MCP Tools for Leads Management
 * 
 * These tools allow MCP clients (Claude, n8n, etc.) to interact
 * with the Aideline Pro CRM leads database
 */

import { MCPTool } from '../types';
import { leadsService } from '../../services/leadsService';
import { Lead } from '../../types';
import { getMCPServer } from '../server/MCPServer';

// ============================================================================
// CREATE LEAD
// ============================================================================

export const createLeadTool: MCPTool = {
  name: 'create_lead',
  description: 'Creates a new lead in the CRM system. Use when user says: "Erstelle Lead", "Neuer Kontakt", "Füge Lead hinzu". A lead represents a potential customer or business opportunity. Returns the created lead with ID.',
  
  inputSchema: {
    type: 'object',
    properties: {
      name: {
        type: 'string',
        description: 'Lead name or company name (required)'
      },
      email: {
        type: 'string',
        description: 'Contact email address'
      },
      phone: {
        type: 'string',
        description: 'Contact phone number'
      },
      company: {
        type: 'string',
        description: 'Company name if different from lead name'
      },
      position: {
        type: 'string',
        description: 'Job title or position'
      },
      status: {
        type: 'string',
        enum: ['new', 'contacted', 'qualified', 'proposal', 'negotiation', 'converted', 'lost'],
        description: 'Current status of the lead'
      },
      source: {
        type: 'string',
        description: 'How the lead was acquired (e.g., "Website", "Referral", "Cold Call")'
      },
      value: {
        type: 'number',
        description: 'Estimated value of the lead in the organization currency'
      },
      notes: {
        type: 'string',
        description: 'Additional notes or context about the lead'
      },
      tags: {
        type: 'array',
        description: 'Tags to categorize the lead',
        items: {
          type: 'string',
          description: 'Tag name'
        }
      }
    },
    required: ['name']
  },

  async execute(params, context) {
    const lead: Omit<Lead, 'id'> = {
      name: params.name,
      email: params.email || '',
      phone: params.phone || '',
      company: params.company || params.name,
      position: params.position || '',
      status: params.status || 'new',
      source: params.source || 'Agent Mode',
      value: params.value || 0,
      notes: params.notes || '',
      tags: params.tags || [],
      createdAt: new Date(),
      updatedAt: new Date(),
      createdBy: context.userId,
      orgId: context.orgId
    };

    const createdLead = await leadsService.createLead(lead);

    return {
      success: true,
      lead: createdLead,
      message: `Lead "${createdLead.name}" created successfully with ID: ${createdLead.id}`
    };
  }
};

// ============================================================================
// GET LEAD
// ============================================================================

export const getLeadTool: MCPTool = {
  name: 'get_lead',
  description: 'Retrieves a specific lead by ID. Use when user asks for details about a specific lead by name or ID. Example: "Zeig mir den Lead Max Mustermann", "Details zu Lead xyz".',
  
  inputSchema: {
    type: 'object',
    properties: {
      leadId: {
        type: 'string',
        description: 'The unique identifier of the lead to retrieve'
      }
    },
    required: ['leadId']
  },

  async execute(params, context) {
    const lead = await leadsService.getLead(params.leadId);

    if (!lead) {
      return {
        success: false,
        error: `Lead with ID ${params.leadId} not found`
      };
    }

    // Check org access
    if (lead.orgId !== context.orgId) {
      return {
        success: false,
        error: 'Access denied: Lead belongs to different organization'
      };
    }

    return {
      success: true,
      lead
    };
  }
};

// ============================================================================
// LIST LEADS
// ============================================================================

export const listLeadsTool: MCPTool = {
  name: 'list_leads',
  description: 'Lists ALL leads for the organization with optional filtering. Use when user asks: "Wie viele Leads?", "Zeig mir alle Leads", "Liste meine Leads", "Welche Leads habe ich?". Supports filtering by status, source, value range. Returns array of leads.',
  
  inputSchema: {
    type: 'object',
    properties: {
      status: {
        type: 'string',
        enum: ['new', 'contacted', 'qualified', 'proposal', 'negotiation', 'converted', 'lost'],
        description: 'Filter leads by status'
      },
      source: {
        type: 'string',
        description: 'Filter leads by source'
      },
      minValue: {
        type: 'number',
        description: 'Filter leads with value greater than or equal to this amount'
      },
      maxValue: {
        type: 'number',
        description: 'Filter leads with value less than or equal to this amount'
      },
      limit: {
        type: 'number',
        description: 'Maximum number of leads to return (default: 50, max: 200)'
      },
      search: {
        type: 'string',
        description: 'Search term to filter leads by name, email, or company'
      }
    }
  },

  async execute(params, context) {
    let leads = await leadsService.getLeads(context.userId, context.orgId);

    // Apply filters
    if (params.status) {
      leads = leads.filter(l => l.status === params.status);
    }

    if (params.source) {
      leads = leads.filter(l => l.source?.toLowerCase().includes(params.source.toLowerCase()));
    }

    if (params.minValue !== undefined) {
      leads = leads.filter(l => (l.value || 0) >= params.minValue);
    }

    if (params.maxValue !== undefined) {
      leads = leads.filter(l => (l.value || 0) <= params.maxValue);
    }

    if (params.search) {
      const searchLower = params.search.toLowerCase();
      leads = leads.filter(l => 
        l.name?.toLowerCase().includes(searchLower) ||
        l.email?.toLowerCase().includes(searchLower) ||
        l.company?.toLowerCase().includes(searchLower)
      );
    }

    // Apply limit
    const limit = Math.min(params.limit || 50, 200);
    leads = leads.slice(0, limit);

    return {
      success: true,
      leads,
      count: leads.length,
      hasMore: leads.length === limit
    };
  }
};

// ============================================================================
// UPDATE LEAD
// ============================================================================

export const updateLeadTool: MCPTool = {
  name: 'update_lead',
  description: 'Updates an existing lead. Use when user says: "Ändere Lead", "Update Lead Status", "Setze Lead auf qualified". Can update any lead field including status, email, phone, notes, tags.',
  
  inputSchema: {
    type: 'object',
    properties: {
      leadId: {
        type: 'string',
        description: 'The unique identifier of the lead to update'
      },
      name: {
        type: 'string',
        description: 'Updated lead name'
      },
      email: {
        type: 'string',
        description: 'Updated email address'
      },
      phone: {
        type: 'string',
        description: 'Updated phone number'
      },
      company: {
        type: 'string',
        description: 'Updated company name'
      },
      position: {
        type: 'string',
        description: 'Updated position'
      },
      status: {
        type: 'string',
        enum: ['new', 'contacted', 'qualified', 'proposal', 'negotiation', 'converted', 'lost'],
        description: 'Updated status'
      },
      source: {
        type: 'string',
        description: 'Updated source'
      },
      value: {
        type: 'number',
        description: 'Updated estimated value'
      },
      notes: {
        type: 'string',
        description: 'Updated or additional notes'
      },
      tags: {
        type: 'array',
        description: 'Updated tags',
        items: {
          type: 'string',
          description: 'Tag name'
        }
      }
    },
    required: ['leadId']
  },

  async execute(params, context) {
    const { leadId, ...updates } = params;

    // Verify lead exists and belongs to org
    const existingLead = await leadsService.getLead(leadId);
    
    if (!existingLead) {
      return {
        success: false,
        error: `Lead with ID ${leadId} not found`
      };
    }

    if (existingLead.orgId !== context.orgId) {
      return {
        success: false,
        error: 'Access denied: Lead belongs to different organization'
      };
    }

    // Update the lead
    await leadsService.updateLead(leadId, {
      ...updates,
      updatedAt: new Date()
    });

    const updatedLead = await leadsService.getLead(leadId);

    return {
      success: true,
      lead: updatedLead,
      message: `Lead "${updatedLead?.name}" updated successfully`
    };
  }
};

// ============================================================================
// DELETE LEAD
// ============================================================================

export const deleteLeadTool: MCPTool = {
  name: 'delete_lead',
  description: 'Deletes a lead permanently from the CRM. Use ONLY when user explicitly says: "Lösche Lead", "Delete Lead", "Entferne Lead". Requires explicit confirmation. This action CANNOT be undone!',
  
  inputSchema: {
    type: 'object',
    properties: {
      leadId: {
        type: 'string',
        description: 'The unique identifier of the lead to delete'
      },
      confirm: {
        type: 'boolean',
        description: 'Must be true to confirm deletion'
      }
    },
    required: ['leadId', 'confirm']
  },

  async execute(params, context) {
    if (!params.confirm) {
      return {
        success: false,
        error: 'Deletion must be confirmed by setting confirm: true'
      };
    }

    // Verify lead exists and belongs to org
    const existingLead = await leadsService.getLead(params.leadId);
    
    if (!existingLead) {
      return {
        success: false,
        error: `Lead with ID ${params.leadId} not found`
      };
    }

    if (existingLead.orgId !== context.orgId) {
      return {
        success: false,
        error: 'Access denied: Lead belongs to different organization'
      };
    }

    await leadsService.deleteLead(params.leadId);

    return {
      success: true,
      message: `Lead "${existingLead.name}" deleted successfully`
    };
  }
};

// ============================================================================
// SEARCH LEADS
// ============================================================================

export const searchLeadsTool: MCPTool = {
  name: 'search_leads',
  description: 'Searches leads using natural language query. Use when user asks: "Suche Lead", "Finde Kontakt von Firma X", "Welche Leads von Microsoft?", "Suche nach Email". Searches across name, email, company, notes, tags. Returns matching leads.',
  
  inputSchema: {
    type: 'object',
    properties: {
      query: {
        type: 'string',
        description: 'Search query (searches name, email, company, notes, tags)'
      },
      limit: {
        type: 'number',
        description: 'Maximum number of results (default: 20)'
      }
    },
    required: ['query']
  },

  async execute(params, context) {
    const leads = await leadsService.getLeads(context.userId, context.orgId);
    const queryLower = params.query.toLowerCase();
    
    const matchedLeads = leads
      .filter(lead => {
        return (
          lead.name?.toLowerCase().includes(queryLower) ||
          lead.email?.toLowerCase().includes(queryLower) ||
          lead.company?.toLowerCase().includes(queryLower) ||
          lead.notes?.toLowerCase().includes(queryLower) ||
          lead.tags?.some(tag => tag.toLowerCase().includes(queryLower))
        );
      })
      .slice(0, params.limit || 20);

    return {
      success: true,
      leads: matchedLeads,
      count: matchedLeads.length,
      query: params.query
    };
  }
};

// ============================================================================
// REGISTER ALL LEAD TOOLS
// ============================================================================

export function registerLeadTools(): void {
  const server = getMCPServer();
  
  server.registerTool(createLeadTool, {
    category: 'leads',
    requiresAuth: true,
    dangerLevel: 'safe'
  });
  
  server.registerTool(getLeadTool, {
    category: 'leads',
    requiresAuth: true,
    dangerLevel: 'safe'
  });
  
  server.registerTool(listLeadsTool, {
    category: 'leads',
    requiresAuth: true,
    dangerLevel: 'safe'
  });
  
  server.registerTool(updateLeadTool, {
    category: 'leads',
    requiresAuth: true,
    dangerLevel: 'moderate'
  });
  
  server.registerTool(deleteLeadTool, {
    category: 'leads',
    requiresAuth: true,
    dangerLevel: 'dangerous'
  });
  
  server.registerTool(searchLeadsTool, {
    category: 'leads',
    requiresAuth: true,
    dangerLevel: 'safe'
  });
  
  console.log('✅ Registered 6 lead management tools');
}

